import clsx from 'clsx';
import {
  type CSSProperties,
  type MutableRefObject,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { PiDotsThree } from 'react-icons/pi';

import ContextMenu from '@/components/ContextMenu/ContextMenu';
import { useSheetDragToClose } from '@/hooks/useSheetDragToClose';

import styles from './MobileModal.module.scss';

type MobileModalProps = {
  open?: boolean;
  title?: ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  maxHeight?: string | number;
  fullScreen?: boolean;
  className?: string;
  containerRef?: MutableRefObject<HTMLElement | null>;
  closable?: boolean;
  haveActions?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  disablePortal?: boolean;
};

export const MobileModal = ({
  open,
  title,
  onClose,
  children,
  maxHeight = '80vh',
  fullScreen = false,
  className,
  containerRef,
  closable = true,
  haveActions = false,
  onEdit,
  onDelete,
  disablePortal = false,
}: MobileModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { dragY, isDragging, onDragStart } = useSheetDragToClose({
    closable,
    onClose: () => {
      setIsAnimating(false);
      setTimeout(() => {
        onClose();
      }, 220);
    },
  });

  const contextMenuItems = [
    { label: 'Edit', onClick: () => onEdit?.() },
    { label: 'Delete', onClick: () => onDelete?.(), variant: 'danger' as const },
  ];

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShouldRender(true);
      setIsAnimating(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
          contentRef.current?.focus();
        });
      });
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 220);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (containerRef) {
      containerRef.current = contentRef.current;
    }
  }, [containerRef]);

  if (typeof document === 'undefined' || !shouldRender) return null;

  const content = (
    <div className={clsx(styles.mobileModal, isAnimating && styles.open)}>
      <div className={styles.backdrop} onClick={closable ? onClose : undefined} />
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        className={clsx(styles.sheet, fullScreen && styles.sheetFullScreen, className)}
        style={
          {
            '--sheet-max-height': typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
            transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
            transition: isDragging ? 'none' : undefined,
          } as CSSProperties
        }
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.dragArea} onPointerDown={onDragStart}>
          <div className={styles.handle} />
          {title && (
            <div className={styles.header}>
              {haveActions ? (
                <button
                  className={styles.actionBtn}
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    setIsContextMenuOpen(!isContextMenuOpen);
                  }}
                >
                  <PiDotsThree />
                </button>
              ) : (
                <div style={{ width: 32, height: 32 }}></div>
              )}

              <div className={styles.title}>{title}</div>

              <div style={{ width: 32, height: 32 }} />
            </div>
          )}

          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className={styles.body}>{children}</div>
        <ContextMenu
          items={contextMenuItems}
          isOpen={isContextMenuOpen}
          onClose={() => setIsContextMenuOpen(false)}
          anchorEl={anchorEl}
        />
      </div>
    </div>
  );

  return disablePortal ? content : createPortal(content, document.body);
};

export default MobileModal;
